import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

// ─── Types ───────────────────────────────────────────────────────────────────
interface JobQuestion {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select';
  required?: boolean;
  options?: string[];
}
interface Job {
  id: string;
  title: string;
  slug: string;
  status: string;
  category: string;
  employmentType: string;
  location: string;
  remote: string;
  pay: string;
  shortDescription: string;
  description: string;
  questions: JobQuestion[];
  /** 'required' (default) | 'optional' | 'hidden' — built-in apply-form fields. */
  resumeRequirement?: string;
  referencesRequirement?: string;
  sortOrder: number;
  postedDate: string;
  metro: string;
  state: string;
  region: string;
  metroTier: number;
  lat: number | null;
  lng: number | null;
  roleFamily: string;
  titleVariant: string;
  /** Number of applicants linked to this job (admin-only). */
  applicantCount?: number;
}
interface Applicant {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  linkedin: string;
  resumeUrl: string;
  resumeFilename: string;
  jobIds: string[];
  stage: string;
  rating: number;
  intro: string;
  references: string;
  answers: { id: string; label: string; answer: string }[];
  notes: string;
  appliedMetro: string;
  titleVariantSeen: string;
  appliedOn: string;
  teacherUid?: string;
}

const STAGES = [
  'Applied',
  'Screening',
  'Interview',
  'Evaluation',
  'Offer',
  'Hired',
  'Archived',
];
const STATUSES = ['Open', 'Draft', 'Closed'];
const CATEGORIES = [
  'Sales & Marketing',
  'Administration & Operations',
  'Teaching',
  'Engineering',
  'Product',
  'Design',
  'Other',
];
const TYPES = ['Part-time', 'Full-time', 'Contract', 'Internship'];
const REMOTES = ['No remote', 'Remote friendly', 'Fully remote'];
const FIELD_REQUIREMENTS = ['required', 'optional', 'hidden'];

const EMPTY_JOB: Job = {
  id: '',
  title: '',
  slug: '',
  status: 'Draft',
  category: 'Teaching',
  employmentType: 'Part-time',
  location: '',
  remote: 'No remote',
  pay: '',
  shortDescription: '',
  description: '',
  questions: [],
  resumeRequirement: 'required',
  referencesRequirement: 'required',
  sortOrder: 100,
  postedDate: new Date().toISOString().slice(0, 10),
  metro: '',
  state: '',
  region: '',
  metroTier: 9,
  lat: null,
  lng: null,
  roleFamily: '',
  titleVariant: '',
};

// ─── Styles (self-contained) ─────────────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  wrap: { padding: '8px 4px' },
  bar: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: '#555' },
  input: { padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 8, fontSize: 14, minWidth: 160 },
  btn: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontWeight: 600, cursor: 'pointer' },
  btnGhost: { padding: '8px 14px', borderRadius: 8, border: '1px solid #d0d0d0', background: '#fff', color: '#333', fontWeight: 600, cursor: 'pointer' },
  btnDanger: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, cursor: 'pointer' },
  card: { border: '1px solid #e5e5e5', borderRadius: 12, padding: 16, marginBottom: 12, background: '#fff' },
  jobRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 },
  meta: { fontSize: 13, color: '#777', display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6 },
  stageTabs: { display: 'flex', gap: 6, flexWrap: 'wrap', margin: '16px 0' },
  stageTab: { padding: '6px 12px', borderRadius: 999, border: '1px solid #d0d0d0', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  stageTabActive: { background: '#4f46e5', color: '#fff', borderColor: '#4f46e5' },
  badge: { background: '#eef2ff', color: '#4f46e5', borderRadius: 999, padding: '2px 8px', fontSize: 12, fontWeight: 700 },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: 24 },
  modal: { background: '#fff', borderRadius: 12, padding: 24, width: 'min(720px, 100%)', maxHeight: '90vh', overflowY: 'auto' },
};

function fn(name: string) {
  return httpsCallable(getFunctions(), name);
}

const ApplicantsAdmin: React.FC<{ initialApplicantId?: string }> = ({ initialApplicantId }) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [catF, setCatF] = useState('all');
  const [typeF, setTypeF] = useState('all');
  const [regionF, setRegionF] = useState('all');
  const [metroF, setMetroF] = useState('all');
  const [statusF, setStatusF] = useState('all');

  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showAllPipeline, setShowAllPipeline] = useState(Boolean(initialApplicantId));
  const [editingJob, setEditingJob] = useState<Job | null>(null);

  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [activeStage, setActiveStage] = useState('Applied');
  const [openApplicant, setOpenApplicant] = useState<Applicant | null>(null);
  const [convertResult, setConvertResult] = useState<{
    email: string;
    temporaryPassword: string | null;
    alreadyExisted: boolean;
    uid: string;
  } | null>(null);
  const [convertingId, setConvertingId] = useState<string | null>(null);
  const [candidateSearchF, setCandidateSearchF] = useState('');
  const [candidateMetroF, setCandidateMetroF] = useState('all');
  const [candidateRatingF, setCandidateRatingF] = useState('all');
  const [candidateJobF, setCandidateJobF] = useState('all');
  const [hireModalOpen, setHireModalOpen] = useState(false);
  const [hireSelectedIds, setHireSelectedIds] = useState<Set<string>>(new Set());
  const [externalHireRows, setExternalHireRows] = useState<Array<{ name: string; email: string; location: string; phone: string }>>([
    { name: '', email: '', location: '', phone: '' },
  ]);
  const [bulkHiring, setBulkHiring] = useState(false);
  const [bulkHireResult, setBulkHireResult] = useState<{
    hiredCount: number;
    failedCount: number;
    results: any[];
  } | null>(null);
  const [bulkHireConfirmOpen, setBulkHireConfirmOpen] = useState(false);
  const [bulkHireConfirmPreview, setBulkHireConfirmPreview] = useState<
    Array<{ source: string; name: string; email: string; location?: string; phone?: string }>
  >([]);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectSelectedIds, setRejectSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRejecting, setBulkRejecting] = useState(false);
  const [bulkRejectResult, setBulkRejectResult] = useState<{
    rejectedCount: number;
    failedCount: number;
    results: any[];
  } | null>(null);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);

  const hirePositionLabel = selectedJob?.title;

  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    setError(null);
    try {
      const res: any = await fn('careersAdminListJobs')({});
      setJobs(res.data.jobs || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load jobs.');
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const loadApplicants = useCallback(async (jobId?: string) => {
    setLoadingApps(true);
    setError(null);
    try {
      const res: any = await fn('careersAdminListApplicants')(jobId ? { jobId } : {});
      setApplicants(res.data.applicants || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load applicants.');
    } finally {
      setLoadingApps(false);
    }
  }, []);

  useEffect(() => {
    if (selectedJob) loadApplicants(selectedJob.id);
    else if (showAllPipeline) loadApplicants();
  }, [selectedJob, showAllPipeline, loadApplicants]);

  useEffect(() => {
    if (!initialApplicantId || loadingApps || applicants.length === 0) return;
    const match = applicants.find((a) => a.id === initialApplicantId);
    if (!match) return;
    setShowAllPipeline(true);
    setSelectedJob(null);
    setActiveStage(match.stage || 'Applied');
    setOpenApplicant(match);
  }, [initialApplicantId, loadingApps, applicants]);

  async function convertApplicantToTeacher(applicant: Applicant) {
    if (!applicant.email) {
      void window.appAlert('This applicant is missing an email address.');
      return;
    }
    if (applicant.teacherUid) {
      const retry = await window.appConfirm(
        `This applicant already has a teacher account (${applicant.teacherUid}).\n\n` +
        `Generate a fresh offer package and send the offer email again?`
      );
      if (!retry) return;
    } else {
      const ok = await window.appConfirm(
        `Hire ${applicant.name} (${applicant.email})?\n\n` +
        `This creates their teacher account (applicant status), generates offer + contract PDFs, ` +
        `and emails them a magic offer link with login credentials.`
      );
      if (!ok) return;
    }

    setConvertingId(applicant.id);
    setConvertResult(null);
    try {
      const res: any = await fn('careersAdminConvertApplicantToTeacher')({
        applicantId: applicant.id,
      });
      const data = res.data || {};
      const updated: Applicant | undefined = data.applicant;
      if (updated) {
        setApplicants((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
        setOpenApplicant((cur) => (cur && cur.id === updated.id ? { ...cur, ...updated } : cur));
        if (updated.stage) setActiveStage(updated.stage);
      }
      setConvertResult({
        email: String(data.email || applicant.email),
        temporaryPassword: data.temporaryPassword || null,
        alreadyExisted: Boolean(data.alreadyExisted),
        uid: String(data.uid || ''),
      });
    } catch (e: any) {
      void window.appAlert(e?.message || 'Failed to convert applicant to teacher.');
    } finally {
      setConvertingId(null);
    }
  }

  const regions = useMemo(
    () => Array.from(new Set(jobs.map((j) => j.region).filter(Boolean))).sort(),
    [jobs]
  );
  const metrosForRegion = useMemo(
    () =>
      Array.from(
        new Set(
          jobs
            .filter((j) => regionF === 'all' || j.region === regionF)
            .map((j) => j.metro)
            .filter(Boolean)
        )
      ).sort(),
    [jobs, regionF]
  );

  const filteredJobs = jobs.filter(
    (j) =>
      (catF === 'all' || j.category === catF) &&
      (typeF === 'all' || j.employmentType === typeF) &&
      (regionF === 'all' || j.region === regionF) &&
      (metroF === 'all' || j.metro === metroF) &&
      (statusF === 'all' || j.status === statusF)
  );

  const jobsById = useMemo(() => new Map(jobs.map((job) => [job.id, job])), [jobs]);
  const totalApplicantCount = useMemo(
    () => jobs.reduce((sum, job) => sum + (job.applicantCount || 0), 0),
    [jobs]
  );

  const applicantPositions = useCallback(
    (applicant: Applicant) =>
      applicant.jobIds
        .map((id) => jobsById.get(id)?.title)
        .filter((title): title is string => Boolean(title))
        .join(', ') || 'Unknown position',
    [jobsById]
  );

  const candidateMetros = useMemo(
    () =>
      Array.from(new Set(applicants.map((a) => a.appliedMetro).filter(Boolean))).sort(),
    [applicants]
  );

  const candidateJobs = useMemo(() => {
    const ids = new Set<string>();
    applicants.forEach((a) => a.jobIds.forEach((id) => ids.add(id)));
    return Array.from(ids)
      .map((id) => ({ id, title: jobsById.get(id)?.title || 'Unknown position' }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [applicants, jobsById]);

  const filteredApplicants = useMemo(() => {
    const q = candidateSearchF.trim().toLowerCase();
    const minRating = candidateRatingF === 'all' ? 0 : Number(candidateRatingF);
    return applicants.filter((a) => {
      if (candidateMetroF !== 'all' && a.appliedMetro !== candidateMetroF) return false;
      if (candidateJobF !== 'all' && !a.jobIds.includes(candidateJobF)) return false;
      if (minRating > 0 && (Number(a.rating) || 0) < minRating) return false;
      if (q) {
        const hay = [a.name, a.firstName, a.lastName, a.email, a.phone, a.appliedMetro]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [applicants, candidateSearchF, candidateMetroF, candidateRatingF, candidateJobF]);

  const stageCountsFiltered = useMemo(() => {
    const c: Record<string, number> = {};
    STAGES.forEach((s) => (c[s] = 0));
    filteredApplicants.forEach((a) => {
      c[a.stage] = (c[a.stage] || 0) + 1;
    });
    return c;
  }, [filteredApplicants]);

  const stageApplicants = filteredApplicants.filter((a) => a.stage === activeStage);

  const hireableApplicants = useMemo(
    () => filteredApplicants.filter((a) => a.stage !== 'Hired' && a.stage !== 'Archived'),
    [filteredApplicants]
  );

  const rejectableApplicants = useMemo(
    () =>
      filteredApplicants.filter(
        (a) => a.stage !== 'Hired' && a.stage !== 'Archived' && !a.teacherUid
      ),
    [filteredApplicants]
  );

  function openHireModal() {
    setHireSelectedIds(new Set());
    setExternalHireRows([{ name: '', email: '', location: '', phone: '' }]);
    setBulkHireResult(null);
    setBulkHireConfirmOpen(false);
    setHireModalOpen(true);
    if (!selectedJob && !showAllPipeline) {
      setShowAllPipeline(true);
      setSelectedJob(null);
    } else if (applicants.length === 0) {
      void loadApplicants(selectedJob?.id);
    }
  }

  function toggleHireSelect(id: string) {
    setHireSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function isValidHireEmail(email: string): boolean {
    return /.+@.+\..+/.test(email.trim());
  }

  function buildBulkHirePreview(): {
    ok: boolean;
    error?: string;
    applicantIds: string[];
    externalHires: Array<{ name: string; email: string; location: string; phone: string }>;
    previewRows: Array<{ source: string; name: string; email: string; location?: string; phone?: string }>;
  } {
    const externalHires = externalHireRows
      .map((row) => ({
        name: row.name.trim(),
        email: row.email.trim().toLowerCase(),
        location: row.location.trim(),
        phone: row.phone.trim(),
      }))
      .filter((row) => row.name || row.email || row.location || row.phone);

    for (const row of externalHires) {
      if (!row.name) {
        return { ok: false, error: 'Each external hire needs a name.', applicantIds: [], externalHires: [], previewRows: [] };
      }
      if (!isValidHireEmail(row.email)) {
        return {
          ok: false,
          error: `Email is required for external hire "${row.name}". Name-only hires are not allowed.`,
          applicantIds: [],
          externalHires: [],
          previewRows: [],
        };
      }
      if (!row.location) {
        return {
          ok: false,
          error: `Location is required for external hire "${row.name}".`,
          applicantIds: [],
          externalHires: [],
          previewRows: [],
        };
      }
    }

    const applicantIds = Array.from(hireSelectedIds);
    const selectedApplicants = hireableApplicants.filter((a) => hireSelectedIds.has(a.id));
    const missingEmail = selectedApplicants.filter((a) => !isValidHireEmail(a.email || ''));
    if (missingEmail.length) {
      return {
        ok: false,
        error: `These applicants need an email before hiring: ${missingEmail
          .map((a) => a.name || a.id)
          .join(', ')}`,
        applicantIds: [],
        externalHires: [],
        previewRows: [],
      };
    }

    if (applicantIds.length === 0 && externalHires.length === 0) {
      return {
        ok: false,
        error: 'Select at least one applicant or enter name, email, and location for an external hire.',
        applicantIds: [],
        externalHires: [],
        previewRows: [],
      };
    }

    const previewRows = [
      ...selectedApplicants.map((a) => ({
        source: 'Pipeline',
        name: a.name || 'Unnamed applicant',
        email: (a.email || '').trim(),
        phone: a.phone || undefined,
      })),
      ...externalHires.map((row) => ({
        source: 'External',
        name: row.name,
        email: row.email,
        location: row.location,
        phone: row.phone || undefined,
      })),
    ];

    return { ok: true, applicantIds, externalHires, previewRows };
  }

  function requestBulkHireConfirm() {
    const preview = buildBulkHirePreview();
    if (!preview.ok) {
      void window.appAlert(preview.error);
      return;
    }
    setBulkHireConfirmPreview(preview.previewRows);
    setBulkHireConfirmOpen(true);
  }

  async function submitBulkHire() {
    const preview = buildBulkHirePreview();
    if (!preview.ok) {
      void window.appAlert(preview.error);
      setBulkHireConfirmOpen(false);
      return;
    }

    setBulkHiring(true);
    setBulkHireResult(null);
    try {
      const res: any = await fn('careersAdminBulkHire')({
        applicantIds: preview.applicantIds,
        externalHires: preview.externalHires,
        jobId: selectedJob?.id || undefined,
      });
      const data = res.data || {};
      setBulkHireResult({
        hiredCount: Number(data.hiredCount || 0),
        failedCount: Number(data.failedCount || 0),
        results: Array.isArray(data.results) ? data.results : [],
      });

      await loadApplicants(selectedJob?.id);
      await loadJobs();
      setHireSelectedIds(new Set());
      setExternalHireRows([{ name: '', email: '', location: '', phone: '' }]);
      setBulkHireConfirmOpen(false);
    } catch (e: any) {
      void window.appAlert(e?.message || 'Bulk hire failed.');
    } finally {
      setBulkHiring(false);
    }
  }

  function openRejectModal() {
    setRejectSelectedIds(new Set());
    setBulkRejectResult(null);
    setRejectConfirmOpen(false);
    setRejectModalOpen(true);
    if (!selectedJob && !showAllPipeline) {
      setShowAllPipeline(true);
      setSelectedJob(null);
    } else if (applicants.length === 0) {
      void loadApplicants(selectedJob?.id);
    }
  }

  function toggleRejectSelect(id: string) {
    setRejectSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submitBulkReject() {
    const applicantIds = Array.from(rejectSelectedIds);
    if (applicantIds.length === 0) {
      setRejectConfirmOpen(false);
      return;
    }
    setBulkRejecting(true);
    setBulkRejectResult(null);
    try {
      const res: any = await fn('careersAdminBulkReject')({ applicantIds });
      const data = res.data || {};
      setBulkRejectResult({
        rejectedCount: Number(data.rejectedCount || 0),
        failedCount: Number(data.failedCount || 0),
        results: Array.isArray(data.results) ? data.results : [],
      });
      await loadApplicants(selectedJob?.id);
      setRejectSelectedIds(new Set());
      setRejectConfirmOpen(false);
    } catch (e: any) {
      void window.appAlert(e?.message || 'Bulk reject failed.');
    } finally {
      setBulkRejecting(false);
    }
  }

  async function saveJob(job: Job) {
    const fields: any = {
      Title: job.title,
      Slug: job.slug,
      Status: job.status,
      Category: job.category,
      'Employment Type': job.employmentType,
      Location: job.location,
      Remote: job.remote,
      Pay: job.pay,
      'Short Description': job.shortDescription,
      Description: job.description,
      'Application Questions': JSON.stringify(job.questions || []),
      'Resume Requirement': job.resumeRequirement || 'required',
      'References Requirement': job.referencesRequirement || 'required',
      'Sort Order': Number(job.sortOrder) || 100,
      'Posted Date': job.postedDate,
    };
    try {
      await fn('careersAdminUpsertJob')({ id: job.id || undefined, fields });
      setEditingJob(null);
      await loadJobs();
    } catch (e: any) {
      void window.appAlert(e?.message || 'Failed to save job.');
    }
  }

  async function updateApplicant(
    id: string,
    patch: { stage?: string; rating?: number; notes?: string }
  ) {
    try {
      const res: any = await fn('careersAdminUpdateApplicant')({ id, ...patch });
      const updated: Applicant = res.data.applicant;
      setApplicants((prev) => prev.map((a) => (a.id === id ? { ...a, ...updated } : a)));
      setOpenApplicant((cur) => (cur && cur.id === id ? { ...cur, ...updated } : cur));
    } catch (e: any) {
      void window.appAlert(e?.message || 'Failed to update applicant.');
    }
  }

  // ─── Job editor (full page) ────────────────────────────────────────────────
  if (editingJob) {
    return (
      <JobEditor
        job={editingJob}
        onCancel={() => setEditingJob(null)}
        onSave={saveJob}
      />
    );
  }

  // ─── Pipeline view ─────────────────────────────────────────────────────────
  if (selectedJob || showAllPipeline) {
    const isAllPipeline = showAllPipeline && !selectedJob;
    return (
      <div style={S.wrap}>
        <button style={S.btnGhost} onClick={() => { setSelectedJob(null); setShowAllPipeline(false); setApplicants([]); }}>
          ← All job posts
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          <h2 style={{ margin: 0 }}>{isAllPipeline ? 'All candidates' : selectedJob?.title}</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={S.btnDanger} onClick={openRejectModal}>Bulk reject</button>
            <button style={S.btn} onClick={openHireModal}>Bulk hire teachers</button>
          </div>
        </div>
        {isAllPipeline ? (
          <div style={S.meta}>
            <span>Every applicant across all job posts</span>
            <span>
              {filteredApplicants.length === applicants.length
                ? `${applicants.length} candidate${applicants.length === 1 ? '' : 's'}`
                : `${filteredApplicants.length} of ${applicants.length} candidates`}
            </span>
          </div>
        ) : selectedJob ? (
          <div style={S.meta}>
            <span>{selectedJob.employmentType}</span>
            <span>{selectedJob.location || '—'}</span>
            <span>{selectedJob.category}</span>
            <span>Status: {selectedJob.status}</span>
            <button style={{ ...S.btnGhost, padding: '2px 10px' }} onClick={() => setEditingJob(selectedJob)}>
              Edit job
            </button>
          </div>
        ) : null}

        <div style={S.bar}>
          <div style={S.field}>
            <span style={S.label}>Search</span>
            <input
              style={{ ...S.input, minWidth: 180 }}
              placeholder="Name, email, phone…"
              value={candidateSearchF}
              onChange={(e) => setCandidateSearchF(e.target.value)}
            />
          </div>
          <div style={S.field}>
            <span style={S.label}>Metro</span>
            <select style={S.input} value={candidateMetroF} onChange={(e) => setCandidateMetroF(e.target.value)}>
              <option value="all">All metros</option>
              {candidateMetros.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div style={S.field}>
            <span style={S.label}>Min rating</span>
            <select style={S.input} value={candidateRatingF} onChange={(e) => setCandidateRatingF(e.target.value)}>
              <option value="all">Any rating</option>
              <option value="1">★ 1+</option>
              <option value="2">★★ 2+</option>
              <option value="3">★★★ 3+</option>
              <option value="4">★★★★ 4+</option>
              <option value="5">★★★★★ 5</option>
            </select>
          </div>
          <div style={S.field}>
            <span style={S.label}>Position</span>
            <select style={S.input} value={candidateJobF} onChange={(e) => setCandidateJobF(e.target.value)}>
              <option value="all">All positions</option>
              {candidateJobs.map((j) => (
                <option key={j.id} value={j.id}>{j.title}</option>
              ))}
            </select>
          </div>
          {(candidateSearchF || candidateMetroF !== 'all' || candidateRatingF !== 'all' || candidateJobF !== 'all') && (
            <button
              style={S.btnGhost}
              onClick={() => {
                setCandidateSearchF('');
                setCandidateMetroF('all');
                setCandidateRatingF('all');
                setCandidateJobF('all');
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        <div style={S.stageTabs}>
          {STAGES.map((s) => (
            <button
              key={s}
              style={{ ...S.stageTab, ...(activeStage === s ? S.stageTabActive : {}) }}
              onClick={() => setActiveStage(s)}
            >
              {s} <span style={{ opacity: 0.8 }}>({stageCountsFiltered[s] || 0})</span>
            </button>
          ))}
        </div>

        {loadingApps && <p>Loading applicants…</p>}
        {!loadingApps && stageApplicants.length === 0 && (
          <p style={{ color: '#888' }}>
            No applicants in “{activeStage}”
            {filteredApplicants.length !== applicants.length ? ' match these filters' : ''}.
          </p>
        )}

        {stageApplicants.map((a) => (
          <div key={a.id} style={S.card}>
            <div style={S.jobRow}>
              <div>
                <strong style={{ fontSize: 16 }}>{a.name}</strong>
                <div style={S.meta}>
                  {isAllPipeline && <span>Position: {applicantPositions(a)}</span>}
                  <span>{a.email}</span>
                  <span>{a.phone}</span>
                  <span>
                    {a.appliedOn ? new Date(a.appliedOn).toLocaleString() : ''}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={S.btnGhost} onClick={() => setOpenApplicant(a)}>
                  View
                </button>
                <select
                  style={S.input}
                  value={a.stage}
                  onChange={(e) => updateApplicant(a.id, { stage: e.target.value })}
                >
                  {STAGES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}

        {openApplicant && (
          <ApplicantModal
            applicant={openApplicant}
            position={isAllPipeline ? applicantPositions(openApplicant) : selectedJob?.title}
            converting={convertingId === openApplicant.id}
            convertResult={convertResult}
            onClose={() => {
              setOpenApplicant(null);
              setConvertResult(null);
            }}
            onUpdate={updateApplicant}
            onConvertToTeacher={() => void convertApplicantToTeacher(openApplicant)}
          />
        )}

        {hireModalOpen && (
          <BulkHireModal
            applicants={hireableApplicants}
            selectedIds={hireSelectedIds}
            externalRows={externalHireRows}
            hiring={bulkHiring}
            result={bulkHireResult}
            loadingApplicants={loadingApps}
            positionLabel={hirePositionLabel}
            confirmOpen={bulkHireConfirmOpen}
            confirmPreview={bulkHireConfirmPreview}
            onToggle={toggleHireSelect}
            onSelectAll={() => setHireSelectedIds(new Set(hireableApplicants.map((a) => a.id)))}
            onClearSelection={() => setHireSelectedIds(new Set())}
            onChangeExternal={(rows) => setExternalHireRows(rows)}
            onRequestConfirm={requestBulkHireConfirm}
            onConfirmHire={() => void submitBulkHire()}
            onCancelConfirm={() => {
              if (bulkHiring) return;
              setBulkHireConfirmOpen(false);
            }}
            onClose={() => {
              if (bulkHiring) return;
              setHireModalOpen(false);
              setBulkHireConfirmOpen(false);
              setBulkHireResult(null);
            }}
          />
        )}

        {rejectModalOpen && (
          <BulkRejectModal
            applicants={rejectableApplicants}
            selectedIds={rejectSelectedIds}
            rejecting={bulkRejecting}
            result={bulkRejectResult}
            loadingApplicants={loadingApps}
            confirmOpen={rejectConfirmOpen}
            onToggle={toggleRejectSelect}
            onSelectAll={() => setRejectSelectedIds(new Set(rejectableApplicants.map((a) => a.id)))}
            onClearSelection={() => setRejectSelectedIds(new Set())}
            onRequestConfirm={() => {
              if (rejectSelectedIds.size === 0) {
                void window.appAlert('Select at least one applicant to reject.');
                return;
              }
              setRejectConfirmOpen(true);
            }}
            onConfirmReject={() => void submitBulkReject()}
            onCancelConfirm={() => {
              if (bulkRejecting) return;
              setRejectConfirmOpen(false);
            }}
            onClose={() => {
              if (bulkRejecting) return;
              setRejectModalOpen(false);
              setRejectConfirmOpen(false);
              setBulkRejectResult(null);
            }}
          />
        )}
      </div>
    );
  }

  // ─── Job list view ─────────────────────────────────────────────────────────
  return (
    <div style={S.wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Job posts</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button style={S.btnDanger} onClick={openRejectModal}>Bulk reject</button>
          <button style={S.btn} onClick={openHireModal}>Bulk hire teachers</button>
          <a href="/app/admin/hiring-templates" style={{ ...S.btnGhost, textDecoration: 'none', display: 'inline-block' }}>
            Hiring templates
          </a>
          <button
            style={S.btnGhost}
            onClick={() => { setShowAllPipeline(true); setSelectedJob(null); setActiveStage('Applied'); }}
          >
            View all pipeline
            <span
              title={`${totalApplicantCount} candidate${totalApplicantCount === 1 ? '' : 's'}`}
              style={{
                marginLeft: 8,
                background: '#eef2ff',
                color: '#4f46e5',
                borderRadius: 999,
                padding: '1px 8px',
                fontSize: 12,
                fontWeight: 700,
                minWidth: 20,
                display: 'inline-block',
                textAlign: 'center',
              }}
            >
              {totalApplicantCount}
            </span>
          </button>
          <button style={S.btn} onClick={() => setEditingJob({ ...EMPTY_JOB })}>
            + New job post
          </button>
        </div>
      </div>

      <div style={S.bar}>
        <div style={S.field}>
          <span style={S.label}>Category</span>
          <select style={S.input} value={catF} onChange={(e) => setCatF(e.target.value)}>
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={S.field}>
          <span style={S.label}>Type</span>
          <select style={S.input} value={typeF} onChange={(e) => setTypeF(e.target.value)}>
            <option value="all">All types</option>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={S.field}>
          <span style={S.label}>Region</span>
          <select
            style={S.input}
            value={regionF}
            onChange={(e) => { setRegionF(e.target.value); setMetroF('all'); }}
          >
            <option value="all">All regions</option>
            {regions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div style={S.field}>
          <span style={S.label}>Metro</span>
          <select style={S.input} value={metroF} onChange={(e) => setMetroF(e.target.value)}>
            <option value="all">All metros</option>
            {metrosForRegion.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div style={S.field}>
          <span style={S.label}>Status</span>
          <select style={S.input} value={statusF} onChange={(e) => setStatusF(e.target.value)}>
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button style={S.btnGhost} onClick={loadJobs}>Refresh</button>
      </div>

      {error && <p style={{ color: '#c00' }}>{error}</p>}
      {loadingJobs && <p>Loading jobs…</p>}
      {!loadingJobs && filteredJobs.length === 0 && (
        <p style={{ color: '#888' }}>No job posts match these filters.</p>
      )}

      {filteredJobs.map((job) => (
        <div key={job.id} style={S.card}>
          <div style={S.jobRow}>
            <div>
              <strong style={{ fontSize: 17 }}>{job.title}</strong>{' '}
              <span style={{ ...S.badge, background: job.status === 'Open' ? '#dcfce7' : '#f1f5f9', color: job.status === 'Open' ? '#15803d' : '#64748b' }}>
                {job.status}
              </span>
              <div style={S.meta}>
                <span>📍 {job.location || '—'}{job.remote && job.remote !== 'No remote' ? ` / ${job.remote}` : ''}</span>
                {job.region && <span>🌎 {job.region} · T{job.metroTier}</span>}
                <span>🗂️ {job.category}</span>
                <span>🕒 {job.employmentType}</span>
                {job.pay && <span>💰 {job.pay}</span>}
                {job.titleVariant && <span>🧪 Variant {job.titleVariant}</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={S.btnGhost} onClick={() => setEditingJob(job)}>Edit</button>
              <button
                style={S.btn}
                onClick={() => { setSelectedJob(job); setActiveStage('Applied'); }}
              >
                View pipeline
                <span
                  title={`${job.applicantCount ?? 0} applicant${job.applicantCount === 1 ? '' : 's'}`}
                  style={{
                    marginLeft: 8,
                    background: 'rgba(255,255,255,0.25)',
                    color: '#fff',
                    borderRadius: 999,
                    padding: '1px 8px',
                    fontSize: 12,
                    fontWeight: 700,
                    minWidth: 20,
                    display: 'inline-block',
                    textAlign: 'center',
                  }}
                >
                  {job.applicantCount ?? 0}
                </span>
              </button>
            </div>
          </div>
        </div>
      ))}

      {hireModalOpen && (
        <BulkHireModal
          applicants={hireableApplicants}
          selectedIds={hireSelectedIds}
          externalRows={externalHireRows}
          hiring={bulkHiring}
          result={bulkHireResult}
          loadingApplicants={loadingApps}
          positionLabel={hirePositionLabel}
          confirmOpen={bulkHireConfirmOpen}
          confirmPreview={bulkHireConfirmPreview}
          onToggle={toggleHireSelect}
          onSelectAll={() => setHireSelectedIds(new Set(hireableApplicants.map((a) => a.id)))}
          onClearSelection={() => setHireSelectedIds(new Set())}
          onChangeExternal={(rows) => setExternalHireRows(rows)}
          onRequestConfirm={requestBulkHireConfirm}
          onConfirmHire={() => void submitBulkHire()}
          onCancelConfirm={() => {
            if (bulkHiring) return;
            setBulkHireConfirmOpen(false);
          }}
          onClose={() => {
            if (bulkHiring) return;
            setHireModalOpen(false);
            setBulkHireConfirmOpen(false);
            setBulkHireResult(null);
          }}
        />
      )}

      {rejectModalOpen && (
        <BulkRejectModal
          applicants={rejectableApplicants}
          selectedIds={rejectSelectedIds}
          rejecting={bulkRejecting}
          result={bulkRejectResult}
          loadingApplicants={loadingApps}
          confirmOpen={rejectConfirmOpen}
          onToggle={toggleRejectSelect}
          onSelectAll={() => setRejectSelectedIds(new Set(rejectableApplicants.map((a) => a.id)))}
          onClearSelection={() => setRejectSelectedIds(new Set())}
          onRequestConfirm={() => {
            if (rejectSelectedIds.size === 0) {
              void window.appAlert('Select at least one applicant to reject.');
              return;
            }
            setRejectConfirmOpen(true);
          }}
          onConfirmReject={() => void submitBulkReject()}
          onCancelConfirm={() => {
            if (bulkRejecting) return;
            setRejectConfirmOpen(false);
          }}
          onClose={() => {
            if (bulkRejecting) return;
            setRejectModalOpen(false);
            setRejectConfirmOpen(false);
            setBulkRejectResult(null);
          }}
        />
      )}
    </div>
  );
};

// ─── Bulk / external hire modal ──────────────────────────────────────────────
const BulkHireModal: React.FC<{
  applicants: Applicant[];
  selectedIds: Set<string>;
  externalRows: Array<{ name: string; email: string; location: string; phone: string }>;
  hiring: boolean;
  loadingApplicants?: boolean;
  positionLabel?: string;
  confirmOpen: boolean;
  confirmPreview: Array<{ source: string; name: string; email: string; location?: string; phone?: string }>;
  result: { hiredCount: number; failedCount: number; results: any[] } | null;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onChangeExternal: (rows: Array<{ name: string; email: string; location: string; phone: string }>) => void;
  onRequestConfirm: () => void;
  onConfirmHire: () => void;
  onCancelConfirm: () => void;
  onClose: () => void;
}> = ({
  applicants,
  selectedIds,
  externalRows,
  hiring,
  loadingApplicants,
  positionLabel,
  confirmOpen,
  confirmPreview,
  result,
  onToggle,
  onSelectAll,
  onClearSelection,
  onChangeExternal,
  onRequestConfirm,
  onConfirmHire,
  onCancelConfirm,
  onClose,
}) => {
  const namedExternalCount = externalRows.filter(
    (r) => r.name.trim() && r.email.trim() && r.location.trim()
  ).length;
  const totalSelected = selectedIds.size + namedExternalCount;

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modal, width: 'min(820px, 100%)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>Hire teachers</h2>
            <p style={{ margin: '6px 0 0', color: '#666', fontSize: 14 }}>
              Bulk-select applicants and/or add people who applied externally.
              Name, email, and location are required for every external hire.
              {positionLabel ? ` Linked job: ${positionLabel}.` : ''}
            </p>
          </div>
          <button style={S.btnGhost} onClick={onClose} disabled={hiring}>Close</button>
        </div>

        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <strong>Applicants in pipeline</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={{ ...S.btnGhost, padding: '4px 10px' }} onClick={onSelectAll} disabled={hiring || applicants.length === 0}>
                Select all
              </button>
              <button type="button" style={{ ...S.btnGhost, padding: '4px 10px' }} onClick={onClearSelection} disabled={hiring || selectedIds.size === 0}>
                Clear
              </button>
            </div>
          </div>

          {loadingApplicants ? (
            <p style={{ color: '#888' }}>Loading applicants…</p>
          ) : applicants.length === 0 ? (
            <p style={{ color: '#888' }}>No hireable applicants loaded. You can still add external hires below.</p>
          ) : (
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e5e5', borderRadius: 10 }}>
              {applicants.map((a) => {
                const checked = selectedIds.has(a.id);
                const hasEmail = /.+@.+\..+/.test(String(a.email || '').trim());
                return (
                  <label
                    key={a.id}
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      padding: '10px 12px',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: hiring ? 'default' : 'pointer',
                      background: checked ? '#eef2ff' : '#fff',
                      opacity: hasEmail ? 1 : 0.7,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={hiring}
                      onChange={() => onToggle(a.id)}
                      style={{ marginTop: 3 }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <strong>{a.name || 'Unnamed applicant'}</strong>
                      <div style={{ ...S.meta, marginTop: 2 }}>
                        <span style={{ color: hasEmail ? undefined : '#b91c1c' }}>
                          {a.email || 'No email — add one before hiring'}
                        </span>
                        <span>{a.stage}</span>
                        {a.phone ? <span>{a.phone}</span> : null}
                      </div>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ marginTop: 20 }}>
          <strong>External applicants</strong>
          <p style={{ margin: '4px 0 10px', color: '#666', fontSize: 13 }}>
            Enter the name, email, and current location for someone who applied outside the careers form. Phone is optional.
          </p>
          {externalRows.map((row, idx) => (
            <div
              key={idx}
              style={{ display: 'grid', gridTemplateColumns: '1.25fr 1.2fr 1.1fr 1fr auto', gap: 8, marginBottom: 8 }}
            >
              <input
                style={S.input}
                placeholder="Full name *"
                value={row.name}
                disabled={hiring}
                onChange={(e) => {
                  const next = [...externalRows];
                  next[idx] = { ...next[idx], name: e.target.value };
                  onChangeExternal(next);
                }}
              />
              <input
                style={S.input}
                placeholder="Email *"
                value={row.email}
                disabled={hiring}
                onChange={(e) => {
                  const next = [...externalRows];
                  next[idx] = { ...next[idx], email: e.target.value };
                  onChangeExternal(next);
                }}
              />
              <input
                style={S.input}
                placeholder="City, state or metro *"
                value={row.location}
                disabled={hiring}
                onChange={(e) => {
                  const next = [...externalRows];
                  next[idx] = { ...next[idx], location: e.target.value };
                  onChangeExternal(next);
                }}
              />
              <input
                style={S.input}
                placeholder="Phone (optional)"
                value={row.phone}
                disabled={hiring}
                onChange={(e) => {
                  const next = [...externalRows];
                  next[idx] = { ...next[idx], phone: e.target.value };
                  onChangeExternal(next);
                }}
              />
              <button
                type="button"
                style={{ ...S.btnGhost, padding: '8px 10px' }}
                disabled={hiring || externalRows.length <= 1}
                onClick={() => onChangeExternal(externalRows.filter((_, i) => i !== idx))}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            style={S.btnGhost}
            disabled={hiring}
            onClick={() => onChangeExternal([...externalRows, { name: '', email: '', location: '', phone: '' }])}
          >
            + Add another person
          </button>
        </div>

        {result && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              borderRadius: 10,
              background: result.failedCount ? '#fff7ed' : '#ecfdf5',
              border: `1px solid ${result.failedCount ? '#fdba74' : '#a7f3d0'}`,
              color: result.failedCount ? '#9a3412' : '#065f46',
            }}
          >
            <strong>
              Hired {result.hiredCount}
              {result.failedCount ? `, ${result.failedCount} failed` : ''}.
            </strong>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13 }}>
              {result.results.map((r, i) => (
                <li key={i}>
                  {r.ok ? '✓' : '✗'} {r.name || r.applicantId || 'Unknown'}
                  {r.email ? ` · ${r.email}` : ''}
                  {r.error ? ` — ${r.error}` : ''}
                  {r.temporaryPassword ? ` · temp password: ${r.temporaryPassword}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={S.btnGhost} onClick={onClose} disabled={hiring}>Close</button>
          <button style={S.btn} onClick={onRequestConfirm} disabled={hiring || totalSelected === 0}>
            {`Review & hire ${totalSelected || ''}`.trim()}
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div
          style={{ ...S.modalOverlay, zIndex: 1100, background: 'rgba(0,0,0,0.55)' }}
          onClick={onCancelConfirm}
        >
          <div
            style={{ ...S.modal, width: 'min(560px, 100%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 8px' }}>Are you sure?</h2>
            <p style={{ margin: '0 0 14px', color: '#555', fontSize: 14 }}>
              You are about to hire {confirmPreview.length} teacher
              {confirmPreview.length === 1 ? '' : 's'}. Each will get a teacher account
              and an offer package email when possible.
            </p>
            <div
              style={{
                maxHeight: 320,
                overflowY: 'auto',
                border: '1px solid #e5e5e5',
                borderRadius: 10,
              }}
            >
              {confirmPreview.map((row, i) => (
                <div
                  key={`${row.email}-${i}`}
                  style={{
                    padding: '10px 12px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <strong>{row.name}</strong>
                  <div style={{ ...S.meta, marginTop: 2 }}>
                    <span>{row.email}</span>
                    {row.location ? <span>{row.location}</span> : null}
                    {row.phone ? <span>{row.phone}</span> : null}
                    <span>{row.source}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={S.btnGhost} onClick={onCancelConfirm} disabled={hiring}>
                Back
              </button>
              <button style={S.btn} onClick={onConfirmHire} disabled={hiring || confirmPreview.length === 0}>
                {hiring ? 'Hiring…' : `Yes, hire ${confirmPreview.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Bulk reject modal ───────────────────────────────────────────────────────
const BulkRejectModal: React.FC<{
  applicants: Applicant[];
  selectedIds: Set<string>;
  rejecting: boolean;
  loadingApplicants?: boolean;
  confirmOpen: boolean;
  result: { rejectedCount: number; failedCount: number; results: any[] } | null;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onRequestConfirm: () => void;
  onConfirmReject: () => void;
  onCancelConfirm: () => void;
  onClose: () => void;
}> = ({
  applicants,
  selectedIds,
  rejecting,
  loadingApplicants,
  confirmOpen,
  result,
  onToggle,
  onSelectAll,
  onClearSelection,
  onRequestConfirm,
  onConfirmReject,
  onCancelConfirm,
  onClose,
}) => {
  const selectedApplicants = applicants.filter((a) => selectedIds.has(a.id));

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modal, width: 'min(720px, 100%)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>Reject applicants</h2>
            <p style={{ margin: '6px 0 0', color: '#666', fontSize: 14 }}>
              Each selected candidate receives the standard rejection email from Overture Careers
              (careers@overtureai.org) with Steven BCC’d, and is moved to Archived.
            </p>
          </div>
          <button style={S.btnGhost} onClick={onClose} disabled={rejecting}>Close</button>
        </div>

        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <strong>Applicants in pipeline</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={{ ...S.btnGhost, padding: '4px 10px' }} onClick={onSelectAll} disabled={rejecting || applicants.length === 0}>
                Select all
              </button>
              <button type="button" style={{ ...S.btnGhost, padding: '4px 10px' }} onClick={onClearSelection} disabled={rejecting || selectedIds.size === 0}>
                Clear
              </button>
            </div>
          </div>

          {loadingApplicants ? (
            <p style={{ color: '#888' }}>Loading applicants…</p>
          ) : applicants.length === 0 ? (
            <p style={{ color: '#888' }}>No rejectable applicants (hired and archived candidates are excluded).</p>
          ) : (
            <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e5e5e5', borderRadius: 10 }}>
              {applicants.map((a) => {
                const checked = selectedIds.has(a.id);
                const hasEmail = /.+@.+\..+/.test(String(a.email || '').trim());
                return (
                  <label
                    key={a.id}
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      padding: '10px 12px',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: rejecting ? 'default' : 'pointer',
                      background: checked ? '#fef2f2' : '#fff',
                      opacity: hasEmail ? 1 : 0.7,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={rejecting}
                      onChange={() => onToggle(a.id)}
                      style={{ marginTop: 3 }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <strong>{a.name || 'Unnamed applicant'}</strong>
                      <div style={{ ...S.meta, marginTop: 2 }}>
                        <span style={{ color: hasEmail ? undefined : '#b91c1c' }}>
                          {a.email || 'No email — cannot send a rejection'}
                        </span>
                        <span>{a.stage}</span>
                        {a.appliedMetro ? <span>{a.appliedMetro}</span> : null}
                      </div>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {result && (
          <div
            style={{
              marginTop: 16,
              padding: 12,
              borderRadius: 10,
              background: result.failedCount ? '#fff7ed' : '#ecfdf5',
              border: `1px solid ${result.failedCount ? '#fdba74' : '#a7f3d0'}`,
              color: result.failedCount ? '#9a3412' : '#065f46',
            }}
          >
            <strong>
              Rejected {result.rejectedCount}
              {result.failedCount ? `, ${result.failedCount} failed` : ''}.
            </strong>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13 }}>
              {result.results.map((r, i) => (
                <li key={i}>
                  {r.ok ? '✓' : '✗'} {r.name || r.applicantId || 'Unknown'}
                  {r.email ? ` · ${r.email}` : ''}
                  {r.error ? ` — ${r.error}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={S.btnGhost} onClick={onClose} disabled={rejecting}>Close</button>
          <button style={S.btnDanger} onClick={onRequestConfirm} disabled={rejecting || selectedIds.size === 0}>
            {`Review & reject ${selectedIds.size || ''}`.trim()}
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div
          style={{ ...S.modalOverlay, zIndex: 1100, background: 'rgba(0,0,0,0.55)' }}
          onClick={onCancelConfirm}
        >
          <div
            style={{ ...S.modal, width: 'min(560px, 100%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 8px' }}>Are you sure?</h2>
            <p style={{ margin: '0 0 14px', color: '#555', fontSize: 14 }}>
              You are about to reject {selectedApplicants.length} applicant
              {selectedApplicants.length === 1 ? '' : 's'}. Each will get the
              “Overture Position — Application Update” email from
              careers@overtureai.org (Steven BCC’d) and be moved to Archived.
              This can’t be unsent.
            </p>
            <div
              style={{
                maxHeight: 320,
                overflowY: 'auto',
                border: '1px solid #e5e5e5',
                borderRadius: 10,
              }}
            >
              {selectedApplicants.map((a) => (
                <div
                  key={a.id}
                  style={{
                    padding: '10px 12px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <strong>{a.name || 'Unnamed applicant'}</strong>
                  <div style={{ ...S.meta, marginTop: 2 }}>
                    <span>{a.email || 'No email'}</span>
                    <span>{a.stage}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={S.btnGhost} onClick={onCancelConfirm} disabled={rejecting}>
                Back
              </button>
              <button style={S.btnDanger} onClick={onConfirmReject} disabled={rejecting || selectedApplicants.length === 0}>
                {rejecting ? 'Rejecting…' : `Yes, reject ${selectedApplicants.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Applicant detail modal ──────────────────────────────────────────────────
const ApplicantModal: React.FC<{
  applicant: Applicant;
  position?: string;
  converting?: boolean;
  convertResult?: {
    email: string;
    temporaryPassword: string | null;
    alreadyExisted: boolean;
    uid: string;
  } | null;
  onClose: () => void;
  onUpdate: (id: string, patch: { stage?: string; rating?: number; notes?: string }) => void;
  onConvertToTeacher: () => void;
}> = ({ applicant, position, converting, convertResult, onClose, onUpdate, onConvertToTeacher }) => {
  const [notes, setNotes] = useState(applicant.notes || '');
  useEffect(() => setNotes(applicant.notes || ''), [applicant.notes]);
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0 }}>{applicant.name}</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {applicant.teacherUid ? (
              <>
                <button
                  style={S.btn}
                  disabled={Boolean(converting)}
                  onClick={onConvertToTeacher}
                >
                  {converting ? 'Sending…' : 'Send / retry offer'}
                </button>
                <a
                  href={`/app/admin/teachers/${encodeURIComponent(applicant.teacherUid)}`}
                  style={{ ...S.btnGhost, textDecoration: 'none', display: 'inline-block' }}
                >
                  Open teacher account
                </a>
              </>
            ) : (
              <button
                style={S.btn}
                disabled={Boolean(converting)}
                onClick={onConvertToTeacher}
              >
                {converting ? 'Hiring…' : 'Hire applicant'}
              </button>
            )}
            <button style={S.btnGhost} onClick={onClose}>Close</button>
          </div>
        </div>

        {convertResult && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 10,
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              color: '#065f46',
            }}
          >
            <strong>
              {convertResult.alreadyExisted
                ? 'Existing account linked — hire email sent.'
                : 'Hired — account created and offer email sent.'}
            </strong>
            <div style={{ marginTop: 6, fontSize: 13 }}>
              Email: <code>{convertResult.email}</code>
            </div>
            {convertResult.temporaryPassword ? (
              <div style={{ marginTop: 4, fontSize: 13 }}>
                Temporary password: <code>{convertResult.temporaryPassword}</code>
                <div style={{ marginTop: 4, opacity: 0.85 }}>
                  Also included in their hire email. Copy now if you need it for support.
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 4, fontSize: 13 }}>
                An Auth account already existed for this email, so the password was left unchanged.
                Their offer email links to login with the existing password.
              </div>
            )}
            {convertResult.uid && (
              <div style={{ marginTop: 8 }}>
                <a href={`/app/admin/teachers/${encodeURIComponent(convertResult.uid)}`}>
                  Open teacher profile →
                </a>
              </div>
            )}
          </div>
        )}
        <div style={S.detailGrid}>
          <div><strong>Email:</strong> {applicant.email}</div>
          <div><strong>Phone:</strong> {applicant.phone}</div>
          <div>
            <strong>LinkedIn:</strong>{' '}
            {applicant.linkedin ? (
              <a href={applicant.linkedin} target="_blank" rel="noreferrer">{applicant.linkedin}</a>
            ) : '—'}
          </div>
          <div>
            <strong>Resume:</strong>{' '}
            {applicant.resumeUrl ? (
              <a href={applicant.resumeUrl} target="_blank" rel="noreferrer">
                {applicant.resumeFilename || 'Download'}
              </a>
            ) : '—'}
          </div>
          <div><strong>Stage:</strong> {applicant.stage}</div>
          {position && <div><strong>Position:</strong> {position}</div>}
          <div>
            <strong>Applied:</strong>{' '}
            {applicant.appliedOn ? new Date(applicant.appliedOn).toLocaleString() : '—'}
          </div>
          <div><strong>Metro:</strong> {applicant.appliedMetro || '—'}</div>
          <div><strong>Saw title variant:</strong> {applicant.titleVariantSeen || '—'}</div>
        </div>

        <div style={{ marginTop: 16 }}>
          <strong>Rating</strong>
          <div style={{ fontSize: 22, cursor: 'pointer' }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <span
                key={n}
                onClick={() => onUpdate(applicant.id, { rating: n })}
                style={{ color: n <= applicant.rating ? '#f59e0b' : '#d1d5db' }}
              >
                ★
              </span>
            ))}
          </div>
        </div>

        {applicant.intro && (
          <div style={{ marginTop: 16 }}>
            <strong>Introduction</strong>
            <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{applicant.intro}</p>
          </div>
        )}
        {applicant.references && (
          <div style={{ marginTop: 16 }}>
            <strong>References</strong>
            <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{applicant.references}</p>
          </div>
        )}
        {applicant.answers?.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <strong>Application questions</strong>
            {applicant.answers.map((qa, i) => (
              <div key={i} style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{qa.label}</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{qa.answer || '—'}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <strong>Notes</strong>
          <textarea
            style={{ ...S.input, width: '100%', minHeight: 80, marginTop: 4 }}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              if (notes !== applicant.notes) onUpdate(applicant.id, { notes });
            }}
          />
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {STAGES.map((s) => (
            <button
              key={s}
              style={{ ...S.stageTab, ...(applicant.stage === s ? S.stageTabActive : {}) }}
              onClick={() => onUpdate(applicant.id, { stage: s })}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Question builder ────────────────────────────────────────────────────────
function newQuestionId(): string {
  return `q_${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;
}

const QuestionBuilder: React.FC<{
  questions: JobQuestion[];
  onChange: (q: JobQuestion[]) => void;
}> = ({ questions, onChange }) => {
  const update = (i: number, patch: Partial<JobQuestion>) =>
    onChange(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const remove = (i: number) => onChange(questions.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () =>
    onChange([
      ...questions,
      { id: newQuestionId(), label: '', type: 'text', required: true },
    ]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={S.label}>Application questions</span>
        <button type="button" style={S.btnGhost} onClick={add}>+ Add question</button>
      </div>

      {questions.length === 0 && (
        <p style={{ color: '#888', fontSize: 13, margin: '4px 0 12px' }}>
          No custom questions yet. Applicants still answer the standard fields
          (name, email, phone, LinkedIn, resume, references, intro). Click
          “Add question” to ask role-specific questions.
        </p>
      )}

      {questions.map((q, i) => (
        <div key={q.id} style={{ ...S.card, background: '#fafafa' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <span style={S.label}>Question {i + 1}</span>
              <input
                style={{ ...S.input, width: '100%', marginTop: 4 }}
                placeholder="e.g. Do you have a car to commute?"
                value={q.label}
                onChange={(e) => update(i, { label: e.target.value })}
              />
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={S.label}>Answer type</span>
                  <select
                    style={S.input}
                    value={q.type}
                    onChange={(e) => update(i, { type: e.target.value as JobQuestion['type'] })}
                  >
                    <option value="text">Short text</option>
                    <option value="textarea">Long text (paragraph)</option>
                    <option value="select">Dropdown (choose one)</option>
                  </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
                  <input
                    type="checkbox"
                    checked={q.required !== false}
                    onChange={(e) => update(i, { required: e.target.checked })}
                  />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Required</span>
                </label>
              </div>

              {q.type === 'select' && (
                <div style={{ marginTop: 10 }}>
                  <span style={S.label}>Dropdown options</span>
                  {(q.options || []).map((opt, oi) => (
                    <div key={oi} style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <input
                        style={{ ...S.input, flex: 1 }}
                        value={opt}
                        placeholder={`Option ${oi + 1}`}
                        onChange={(e) => {
                          const options = [...(q.options || [])];
                          options[oi] = e.target.value;
                          update(i, { options });
                        }}
                      />
                      <button
                        type="button"
                        style={S.btnGhost}
                        onClick={() =>
                          update(i, {
                            options: (q.options || []).filter((_, x) => x !== oi),
                          })
                        }
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    style={{ ...S.btnGhost, marginTop: 8 }}
                    onClick={() => update(i, { options: [...(q.options || []), ''] })}
                  >
                    + Add option
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button type="button" style={S.btnGhost} title="Move up" onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
              <button type="button" style={S.btnGhost} title="Move down" onClick={() => move(i, 1)} disabled={i === questions.length - 1}>↓</button>
              <button type="button" style={{ ...S.btnGhost, color: '#c00' }} title="Delete" onClick={() => remove(i)}>🗑</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Job editor (full page) ──────────────────────────────────────────────────
const JobEditor: React.FC<{
  job: Job;
  onCancel: () => void;
  onSave: (job: Job) => void;
}> = ({ job, onCancel, onSave }) => {
  const [f, setF] = useState<Job>({ ...job, questions: job.questions || [] });
  const [qError, setQError] = useState<string | null>(null);
  const set = (k: keyof Job, v: any) => setF((p) => ({ ...p, [k]: v }));

  function handleSave() {
    if (!f.title || !f.slug) {
      setQError('Title and Slug are required.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const cleaned = f.questions
      .map((q) => ({
        ...q,
        id: q.id || newQuestionId(),
        label: q.label.trim(),
        options:
          q.type === 'select'
            ? (q.options || []).map((o) => o.trim()).filter(Boolean)
            : undefined,
      }))
      .filter((q) => q.label.length > 0);
    onSave({ ...f, questions: cleaned });
  }

  const row: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 };

  return (
    <div style={S.wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <button style={S.btnGhost} onClick={onCancel}>← Back to job posts</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btnGhost} onClick={onCancel}>Cancel</button>
          <button style={S.btn} onClick={handleSave}>Save job</button>
        </div>
      </div>

      <h2 style={{ marginTop: 0 }}>{f.id ? 'Edit job' : 'New job post'}</h2>
      {qError && <p style={{ color: '#c00' }}>{qError}</p>}

      <div style={row}>
        <span style={S.label}>Title *</span>
        <input style={S.input} value={f.title} onChange={(e) => set('title', e.target.value)} />
      </div>
      <div style={row}>
        <span style={S.label}>Slug * (URL: /careers/&lt;slug&gt;)</span>
        <input style={S.input} value={f.slug} onChange={(e) => set('slug', e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={row}>
          <span style={S.label}>Status</span>
          <select style={S.input} value={f.status} onChange={(e) => set('status', e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={row}>
          <span style={S.label}>Category</span>
          <select style={S.input} value={f.category} onChange={(e) => set('category', e.target.value)}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={row}>
          <span style={S.label}>Employment Type</span>
          <select style={S.input} value={f.employmentType} onChange={(e) => set('employmentType', e.target.value)}>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={row}>
          <span style={S.label}>Remote</span>
          <select style={S.input} value={f.remote} onChange={(e) => set('remote', e.target.value)}>
            {REMOTES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={row}>
          <span style={S.label}>Location</span>
          <input style={S.input} value={f.location} onChange={(e) => set('location', e.target.value)} />
        </div>
        <div style={row}>
          <span style={S.label}>Pay</span>
          <input style={S.input} value={f.pay} onChange={(e) => set('pay', e.target.value)} />
        </div>
        <div style={row}>
          <span style={S.label}>Sort Order</span>
          <input
            type="number"
            style={S.input}
            value={f.sortOrder}
            onChange={(e) => set('sortOrder', Number(e.target.value))}
          />
        </div>
        <div style={row}>
          <span style={S.label}>Posted Date</span>
          <input
            type="date"
            style={S.input}
            value={f.postedDate}
            onChange={(e) => set('postedDate', e.target.value)}
          />
        </div>
      </div>
      <div style={row}>
        <span style={S.label}>Short Description</span>
        <textarea
          style={{ ...S.input, minHeight: 50, width: '100%' }}
          value={f.shortDescription}
          onChange={(e) => set('shortDescription', e.target.value)}
        />
      </div>
      <div style={row}>
        <span style={S.label}>Description (Markdown)</span>
        <textarea
          style={{ ...S.input, minHeight: 200, width: '100%', fontFamily: 'monospace', fontSize: 13 }}
          value={f.description}
          onChange={(e) => set('description', e.target.value)}
        />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={row}>
          <span style={S.label}>Resume field</span>
          <select
            style={S.input}
            value={f.resumeRequirement || 'required'}
            onChange={(e) => set('resumeRequirement', e.target.value)}
          >
            {FIELD_REQUIREMENTS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div style={row}>
          <span style={S.label}>References field</span>
          <select
            style={S.input}
            value={f.referencesRequirement || 'required'}
            onChange={(e) => set('referencesRequirement', e.target.value)}
          >
            {FIELD_REQUIREMENTS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <div style={{ ...S.card, marginTop: 16 }}>
        <QuestionBuilder
          questions={f.questions}
          onChange={(questions) => set('questions', questions)}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button style={S.btnGhost} onClick={onCancel}>Cancel</button>
        <button style={S.btn} onClick={handleSave}>Save job</button>
      </div>
    </div>
  );
};

export default ApplicantsAdmin;
